import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { colors } from '../theme';
import {
  ImportDecisions,
  ParsedRecord,
  RentRollParseResponse,
  deriveLayoutNames,
  planKey,
} from '../utils/rentRollImport';

interface RentRollReviewProps {
  response: RentRollParseResponse;
  decisions: ImportDecisions;
  onDecisionsChange: (decisions: ImportDecisions) => void;
  onImport: () => void;
  onStartOver: () => void;
  importing?: boolean;
}

const formatNumber = (value: number | null | undefined) => {
  if (value === null || value === undefined) return '—';
  const rounded = Math.round(value * 100) / 100;
  return rounded.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

/** Average in-place rent across a plan's paying units. */
const averageInPlaceRent = (records: ParsedRecord[]) => {
  const paying = records.filter((r) => !r.vacant && (r.current_rent_total || 0) > 0);
  if (!paying.length) return 0;
  return Math.round(
    paying.reduce((sum, r) => sum + (r.current_rent_total || 0), 0) / paying.length
  );
};

const RentRollReview: React.FC<RentRollReviewProps> = ({
  response,
  decisions,
  onDecisionsChange,
  onImport,
  onStartOver,
  importing = false,
}) => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  const layoutNames = deriveLayoutNames(response.floor_plans);
  const reconciliation = response.reconciliation;

  // Count UNITS, not rows. A floor-plan row stands for several, so counting
  // rows would promise the user 7 units and import 74.
  const unitsIn = (records: typeof response.records) =>
    records.reduce((total, r) => total + (r.unit_count ?? 1), 0);

  const rowsArePlans = response.rows_are === 'plan';
  const excludedCount = unitsIn(response.records.filter((r) => r.excluded));
  const vacantCount = unitsIn(response.records.filter((r) => r.vacant && !r.excluded));
  const importCount =
    unitsIn(response.records) -
    excludedCount -
    (decisions.vacantUnits === 'exclude' ? vacantCount : 0);

  const setDecision = (patch: Partial<ImportDecisions>) =>
    onDecisionsChange({ ...decisions, ...patch });

  const mixRows = response.floor_plans.map((plan) => {
    const key = planKey(plan.type, plan.sqft);
    const planRecords = response.records.filter(
      (r) => planKey(r.type, r.sqft) === key
    );
    return {
      key,
      layout: layoutNames[key],
      sqft: plan.sqft,
      count: plan.unit_count,
      avgInPlace: averageInPlaceRent(planRecords),
      marketRent: plan.market_rent,
    };
  });

  const gridColumns = 'minmax(0, 1.4fr) 90px 80px minmax(0, 1fr) minmax(0, 1fr)';

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
      {/* ── Reconciliation ─────────────────────────────────────────────── */}
      <Typography sx={{ fontWeight: 700, color: colors.grey[900], mb: 1 }}>
        What we read from your file
      </Typography>

      {/* Which page a PDF was read from, on EVERY PDF import. A rent roll is
          rarely the biggest table in an offering memorandum, so "we picked
          page 14 of 18" is the difference between trusting the number and
          checking it. */}
      {response.page ? (
        <Typography variant="body2" sx={{ color: colors.grey[600], mb: 2 }}>
          Read from page {response.page} of your PDF.
        </Typography>
      ) : null}

      {rowsArePlans && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This file lists floor plans rather than individual units. We'll create{' '}
          {importCount.toLocaleString()} units from {response.records.length} rows — they
          won't have unit numbers, and each one starts at its plan's average rent.
        </Alert>
      )}

      {!reconciliation.all_ok && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          We couldn't fully verify this file — review carefully before importing.
        </Alert>
      )}

      <Box
        sx={{
          border: `1px solid ${colors.grey[300]}`,
          borderRadius: 2,
          p: 1.5,
          mb: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
        }}
      >
        {reconciliation.checks.map((check) => {
          // Three states, not two. A check the file gives us nothing to compare
          // against is NOT a failure — rendering it in red with an error icon
          // said something was wrong when nothing was, and the value it printed
          // was an em-dash, so it read as an unfinished sentence.
          const nothingToCheck = check.reported === null || check.reported === undefined;
          return (
            <Box key={check.name} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              {check.ok ? (
                <CheckCircleOutlineIcon fontSize="small" sx={{ color: colors.success, mt: '2px' }} />
              ) : nothingToCheck ? (
                <RemoveCircleOutlineIcon fontSize="small" sx={{ color: colors.grey[400], mt: '2px' }} />
              ) : (
                <ErrorOutlineIcon fontSize="small" sx={{ color: colors.error, mt: '2px' }} />
              )}
              <Typography variant="body2" sx={{ color: colors.grey[900] }}>
                <strong>{formatNumber(check.parsed)}</strong> {check.label.toLowerCase()}
                {check.ok ? (
                  <span style={{ color: colors.grey[600] }}> — matches the report's own total</span>
                ) : nothingToCheck ? (
                  <span style={{ color: colors.grey[600] }}>
                    {' '}
                    — your file doesn't state a total for this, so we couldn't check it
                  </span>
                ) : (
                  <span style={{ color: colors.error }}>
                    {' '}
                    — your file says {formatNumber(check.reported)}
                  </span>
                )}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* ── Derived unit mix ───────────────────────────────────────────── */}
      <Typography sx={{ fontWeight: 700, color: colors.grey[900], mb: 1 }}>
        Floor plans we found
      </Typography>

      {isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
          {mixRows.map((row) => (
            <Box
              key={row.key}
              sx={{ border: `1px solid ${colors.grey[300]}`, borderRadius: 2, p: 1.5 }}
            >
              <Typography sx={{ fontWeight: 600 }}>{row.layout}</Typography>
              <Typography variant="body2" sx={{ color: colors.grey[600] }}>
                {row.sqft ? `${row.sqft.toLocaleString()} SF` : 'No SF'} · {row.count} units
              </Typography>
              <Typography variant="body2" sx={{ color: colors.grey[700] }}>
                Avg in-place ${row.avgInPlace.toLocaleString()} · Market $
                {formatNumber(row.marketRent)}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : (
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: gridColumns,
              gap: 1.5,
              px: 1.5,
              py: 0.5,
              borderBottom: `1px solid ${colors.grey[300]}`,
            }}
          >
            {['Floor Plan', 'SF', 'Units', 'Avg In-Place Rent', 'Market Rent'].map((h) => (
              <Typography
                key={h}
                variant="caption"
                sx={{ fontSize: 13, fontWeight: 700, color: 'text.secondary' }}
              >
                {h}
              </Typography>
            ))}
          </Box>
          {mixRows.map((row) => (
            <Box
              key={row.key}
              sx={{
                display: 'grid',
                gridTemplateColumns: gridColumns,
                gap: 1.5,
                px: 1.5,
                py: 0.75,
                alignItems: 'center',
                borderBottom: `1px solid ${colors.grey[100]}`,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.layout}</Typography>
              <Typography variant="body2">{row.sqft ? row.sqft.toLocaleString() : '—'}</Typography>
              <Typography variant="body2">{row.count}</Typography>
              <Typography variant="body2">${row.avgInPlace.toLocaleString()}</Typography>
              <Typography variant="body2">${formatNumber(row.marketRent)}</Typography>
            </Box>
          ))}
        </Box>
      )}

      <Divider sx={{ mb: 3 }} />

      {/* ── Decisions ──────────────────────────────────────────────────── */}
      <Typography sx={{ fontWeight: 700, color: colors.grey[900], mb: 1.5 }}>
        How should we set this up?
      </Typography>

      <FormControl sx={{ mb: 2, display: 'block' }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          Market rents
        </Typography>
        <RadioGroup
          value={decisions.marketRents}
          onChange={(e) => setDecision({ marketRents: e.target.value as ImportDecisions['marketRents'] })}
        >
          <FormControlLabel
            value="use_rent_roll"
            control={<Radio size="small" />}
            label="Use the rent roll's market rents"
          />
          <FormControlLabel
            value="enter_my_own"
            control={<Radio size="small" />}
            label="I'll enter my own by plan"
          />
        </RadioGroup>
      </FormControl>

      <FormControl sx={{ mb: 2, display: 'block' }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          At lease expiration
        </Typography>
        <RadioGroup
          value={decisions.atExpiration}
          onChange={(e) => setDecision({ atExpiration: e.target.value as ImportDecisions['atExpiration'] })}
        >
          <FormControlLabel value="market_adjustment" control={<Radio size="small" />} label="Market adjustment" />
          <FormControlLabel value="vacate" control={<Radio size="small" />} label="Vacate & re-lease" />
          <FormControlLabel value="keep" control={<Radio size="small" />} label="Keep in place" />
        </RadioGroup>
      </FormControl>

      <FormControl sx={{ mb: 1, display: 'block' }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          Vacant units ({vacantCount})
        </Typography>
        <RadioGroup
          value={decisions.vacantUnits}
          onChange={(e) => setDecision({ vacantUnits: e.target.value as ImportDecisions['vacantUnits'] })}
        >
          <FormControlLabel value="re_lease" control={<Radio size="small" />} label="Re-lease at market, month 1" />
          <FormControlLabel value="exclude" control={<Radio size="small" />} label="Exclude from the model" />
        </RadioGroup>
      </FormControl>

      {excludedCount > 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {excludedCount} down or off-line unit{excludedCount === 1 ? '' : 's'} (marked
          "Vacant Unit*") {excludedCount === 1 ? 'is' : 'are'} left out of the rent roll, matching
          how the report excludes {excludedCount === 1 ? 'it' : 'them'} from its own occupancy
          totals.
        </Alert>
      )}

      {/* ── Exceptions ─────────────────────────────────────────────────── */}
      {response.exceptions.length > 0 && (
        <>
          <Typography sx={{ fontWeight: 700, color: colors.grey[900], mb: 1 }}>
            Worth a look
          </Typography>
          <Box
            sx={{
              border: `1px solid ${colors.grey[300]}`,
              borderRadius: 2,
              p: 1.5,
              mb: 3,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            {response.exceptions.map((exception) => (
              <Box key={exception.kind}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Chip size="small" label={exception.count} color="warning" variant="outlined" />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {exception.label}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: colors.grey[600] }}>
                  {exception.unit_labels.slice(0, 12).join(', ')}
                  {exception.unit_labels.length > 12
                    ? ` and ${exception.unit_labels.length - 12} more`
                    : ''}
                </Typography>
              </Box>
            ))}
          </Box>
        </>
      )}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button variant="contained" onClick={onImport} disabled={importing || importCount === 0}>
          {importing ? 'Importing…' : `Import ${importCount.toLocaleString()} units`}
        </Button>
        <Button variant="text" onClick={onStartOver} disabled={importing}>
          Upload a different file
        </Button>
      </Box>
    </Box>
  );
};

export default RentRollReview;
